import { ExpandMore } from "@mui/icons-material";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, useTheme } from "@mui/material";
import useEnhancedEffect from "@mui/material/utils/useEnhancedEffect";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getOrDefault } from "../../Helpers/Utils";
import { useMicrospecThemeLanguageContext } from "../../Themes";
import DrawerListChildrenItems from "./DrawerListChildrenItems";

// Helper function to convert hex color to RGB
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '102, 126, 234'; // fallback
};

const DrawerListParentItem = ({ value, data }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { mobileOpen, handleDrawerToggle, dashboard, item } = data;
    const childrenItems = getOrDefault(value.specs, []);
    const hasChildrenItems = childrenItems.length > 0;
    const childrenItemsPaths = childrenItems.map((item) => item.fullPath);
    const [childrenOpen, setChildrenOpen] = useState(childrenItemsPaths.includes(item.fullPath));
    const [isHovered, setIsHovered] = useState(false);

    const isSelected = item.path === value.path;
    const hasSelectedChild = childrenItemsPaths.includes(location.pathname);

    useEnhancedEffect(() => {
        if (childrenOpen && !childrenItemsPaths.includes(location.pathname)) {
            setChildrenOpen(false);
        }
    }, [location.pathname]);

    const handleClick = () => {
        if (!hasChildrenItems) {
            if (mobileOpen) {
                handleDrawerToggle();
            }
            if (value.path !== item.path) {
                navigate(dashboard.path + "/" + value.path, { replace: true, preventScrollReset: false });
            }
        } else {
            setChildrenOpen(!childrenOpen);
        }
    };

    return (
        <List
            sx={{ 
                width: '100%', 
                maxWidth: 360, 
                my: 0, 
                py: 0.5,
                px: 0.5,
            }}
            component="nav"
            aria-labelledby="nested-list-subheader"
        >
            <Tooltip 
                title={localizationProvider.translateByType(value.name)} 
                placement="right"
                arrow
                disableHoverListener={!isHovered}
            >
                <ListItemButton
                    id={value.path + "list-item-button"}
                    key={value.path + "list-item-button"}
                    name={value.path + "list-item-button"}
                    onClick={handleClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    selected={isSelected}
                    sx={{
                        borderRadius: '12px',
                        mx: 0.5,
                        mb: 0.5,
                        minHeight: 48,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                        
                        // Base styling - using theme primary and secondary colors
                        background: isSelected || hasSelectedChild
                            ? (theme) => {
                                const primaryRgb = hexToRgb(theme.palette.primary.main);
                                const secondaryRgb = hexToRgb(theme.palette.secondary.main);
                                const opacity = theme.palette.mode === 'dark' ? '0.2' : '0.15';
                                return `linear-gradient(135deg, rgba(${primaryRgb}, ${opacity}) 0%, rgba(${secondaryRgb}, ${opacity}) 100%)`;
                            }
                            : 'transparent',
                        
                        // Border and shadow
                        border: (theme) => {
                            if (theme.palette.mode === 'dark') {
                                if (isSelected || hasSelectedChild) {
                                    const primaryRgb = hexToRgb(theme.palette.primary.main);
                                    return `1px solid rgba(${primaryRgb}, 0.3)`;
                                }
                                return '1px solid rgba(255, 255, 255, 0.05)';
                            } else {
                                if (isSelected || hasSelectedChild) {
                                    const primaryRgb = hexToRgb(theme.palette.primary.main);
                                    return `1px solid rgba(${primaryRgb}, 0.2)`;
                                }
                                return '1px solid rgba(0, 0, 0, 0.03)';
                            }
                        },
                        
                        boxShadow: isSelected || hasSelectedChild
                            ? (theme) => {
                                const primaryRgb = hexToRgb(theme.palette.primary.main);
                                if (theme.palette.mode === 'dark') {
                                    return `0 4px 20px rgba(${primaryRgb}, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)`;
                                }
                                return `0 4px 20px rgba(${primaryRgb}, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)`;
                            }
                            : 'none',

                        // Hover effects
                        '&:hover': {
                            background: isSelected || hasSelectedChild
                                ? (theme) => {
                                    const primaryRgb = hexToRgb(theme.palette.primary.main);
                                    const secondaryRgb = hexToRgb(theme.palette.secondary.main);
                                    const opacity = theme.palette.mode === 'dark' ? '0.3' : '0.25';
                                    return `linear-gradient(135deg, rgba(${primaryRgb}, ${opacity}) 0%, rgba(${secondaryRgb}, ${opacity}) 100%)`;
                                }
                                : (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.03)',
                            transform: 'translateX(4px)',
                            boxShadow: (theme) => {
                                const primaryRgb = hexToRgb(theme.palette.primary.main);
                                if (theme.palette.mode === 'dark') {
                                    return `0 6px 25px rgba(${primaryRgb}, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)`;
                                }
                                return `0 6px 25px rgba(${primaryRgb}, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.9)`;
                            },
                        },

                        // Pressed state
                        '&:active': {
                            transform: 'translateX(2px) scale(0.98)',
                        },

                        // Backdrop effect for selected state
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: isSelected || hasSelectedChild
                                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                                : 'transparent',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            pointerEvents: 'none',
                        }
                    }}
                >
                    {/* Left indicator for active state */}
                    {(isSelected || hasSelectedChild) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 4,
                                height: '60%',
                                borderRadius: '0 4px 4px 0',
                                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                boxShadow: (theme) => {
                                    const primaryRgb = hexToRgb(theme.palette.primary.main);
                                    return `0 2px 8px rgba(${primaryRgb}, 0.4)`;
                                },
                            }}
                        />
                    )}

                    {/* Icon */}
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            color: isSelected || hasSelectedChild
                                ? (theme) => theme.palette.mode === 'dark' 
                                    ? theme.palette.primary.light 
                                    : theme.palette.primary.dark
                                : theme.palette.text.secondary,
                            transition: 'all 0.3s ease',
                            '& .MuiSvgIcon-root': {
                                fontSize: 22,
                                filter: isSelected || hasSelectedChild
                                    ? (theme) => {
                                        const primaryRgb = hexToRgb(theme.palette.primary.main);
                                        return `drop-shadow(0 2px 4px rgba(${primaryRgb}, 0.3))`;
                                    }
                                    : 'none',
                            }
                        }}
                    >
                        {value.icon}
                    </ListItemIcon>

                    {/* Text */}
                    <ListItemText
                        id={value.path + "list-item-text"}
                        key={value.path + "list-item-text"}
                        name={value.path + "list-item-text"}
                        primary={localizationProvider.translateByType(value.name)}
                        sx={{
                            display: "flex", 
                            justifyContent: "flex-start",
                            '& .MuiListItemText-primary': {
                                fontSize: '0.9rem',
                                fontWeight: isSelected || hasSelectedChild ? 600 : 500,
                                color: isSelected || hasSelectedChild
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                                transition: 'all 0.3s ease',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }
                        }}
                    />

                    {/* Expand/Collapse icon with enhanced styling */}
                    {hasChildrenItems && (
                        <ListItemIcon
                            sx={{
                                minWidth: 'auto',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.03)',
                                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                transform: childrenOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                '&:hover': {
                                    background: (theme) => theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.06)',
                                    transform: childrenOpen ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                                }
                            }}
                        >
                            <ExpandMore
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </ListItemIcon>
                    )}
                </ListItemButton>
            </Tooltip>
            
            {/* Children items with enhanced styling */}
            {hasChildrenItems && (
                <DrawerListChildrenItems 
                    data={{ 
                        parent: value, 
                        childrenItems, 
                        childrenOpen, 
                        setChildrenOpen 
                    }} 
                />
            )}
        </List>
    );
};

export default DrawerListParentItem;